// Chakra Imports
import { Box, Flex, Text, useColorModeValue, Icon, Button, Switch, HStack, Card, CardBody, SimpleGrid, VStack, Menu, MenuButton, MenuList, MenuItem, useDisclosure, Popover, PopoverTrigger, PopoverContent, PopoverBody, Img } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import AdminNavbarLinks from 'components/navbar/NavbarLinksAdmin';
import { MdDashboard, MdAdd, MdDescription, MdAttachMoney, MdCheckCircle, MdPeople, MdBusiness, MdWork, MdMedicalServices, MdDirectionsCar, MdBarChart, MdArrowDropDown, MdCalendarToday, MdChevronLeft, MdChevronRight, MdPerson } from 'react-icons/md';
import { IoMdArrowForward } from 'react-icons/io';
import { FaTrophy } from 'react-icons/fa';
import { useNavbarAction } from 'contexts/NavbarActionContext';
import { useAnalyses } from 'contexts/AnalysesContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from 'hooks/usePermissions';
import routes from 'routes.js';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import 'assets/css/AnalysesDatePicker.css';
import plusSquaredIcon from "assets/img/icons/Dashboard-icon/plus-squared.png"


export default function AdminNavbar(props) {
	const [scrolled, setScrolled] = useState(false);
	const navbarRef = React.useRef(null);
	const location = useLocation();
	const decodedPathname = decodeURIComponent(location.pathname);
	const navigate = useNavigate();

	// Fonction utilitaire pour vérifier si une page est active
	const isPageActive = (pathSegments) => {
		if (Array.isArray(pathSegments)) {
			return pathSegments.some(segment => decodedPathname.includes(segment));
		}
		return decodedPathname.includes(pathSegments);
	};

	// Page detectors basés sur la structure des routes
	const isMissionsPage = isPageActive('/missions');
	const isAnalysesPage = isPageActive('/analyses');
	const isPatientsPage = isPageActive('/patients');
	const isUtilisateursPage = isPageActive('/utilisateurs');
	const isVehiculesPage = isPageActive('/vehicules');
	const isMaterielsPage = isPageActive('/materiels');
	const isProfilPage = isPageActive('/profile');
	const isAvisPage = isPageActive('/avis');
	const isReclamationsPage = isPageActive('/reclamations');
	const isPointageSuiviPage = isPageActive('/pointage-suivi');
	const isLogsPage = isPageActive('/logs');

	// Pages alternatives/archived
	const isProspectsPage = isPageActive('/prospects');
	const isAdherentPage = isPageActive(['/adhérents', '/adh%C3%A9rents']);
	const isClientsPage = isPageActive('/clients');

	const { brandText } = props;
	const { hasPermission, permissions, loading: permissionsLoading } = usePermissions();
	const normalizedPermissions = React.useMemo(
		() => (Array.isArray(permissions) ? permissions.map((p) => String(p).trim().toLowerCase()) : []),
		[permissions]
	);
	const canCreateMission = !permissionsLoading && (
		hasPermission('missions_create') || normalizedPermissions.includes('missions_create') || normalizedPermissions.includes('allaccess')
	);
	const { showStats, setShowStats, onCreateProspect, onCreateAdherent, onCreatePatient, onCreateClient, onCreateUtilisateur, onCreateMateriel, onCreateVehicule, onCreateReclamation, stats, setNavbarHeight } = useNavbarAction();

	// États pour la période couverte (page Analyses) - depuis le contexte
	const { selectedPeriod, setSelectedPeriod, dateRange, setDateRange, selectedVille, setSelectedVille, selectedRegion, setSelectedRegion, selectedAnalyses, setSelectedAnalyses, toggleSelectedAnalysis } = useAnalyses();
	const analysesOptions = [
		{ key: 'medicale', label: 'Analyse Médicale' },
		{ key: 'medecins-prescripteurs', label: 'Médecins Prescripteurs' },
		{ key: 'missions', label: 'Missions' },
		{ key: 'flotte', label: 'Flotte & Maintenance' },
		{ key: 'conformite', label: 'Qualité & Conformité' },
		{ key: 'ambulances', label: 'Analyse des Ambulances' },
		{ key: 'geographique', label: 'Analyse Géographique' },
		{ key: 'performance', label: 'Performance' }
	];
	const selectedAnalysesList = Array.isArray(selectedAnalyses) ? selectedAnalyses : [selectedAnalyses];
	const isAllAnalysesSelected = selectedAnalysesList.includes('tous');
	const selectedAnalysesLabel = isAllAnalysesSelected
		? 'Tous les Analyses…'
		: selectedAnalysesList.length === 0
			? 'Tous les Analyses…'
			: selectedAnalysesList.length === 1
				? (analysesOptions.find((option) => option.key === selectedAnalysesList[0])?.label || 'Tous les Analyses…')
				: `${selectedAnalysesList.length} analyses sélectionnées`;
	const handleCreateMissionClick = React.useCallback(() => {
		if (!canCreateMission) return;
		navigate('/admin/dossier-regulation/create');
	}, [canCreateMission, navigate]);
	const [calendarMonth, setCalendarMonth] = useState(new Date());
	const [calendarMonth2, setCalendarMonth2] = useState(() => {
		const nextMonth = new Date();
		nextMonth.setMonth(nextMonth.getMonth() + 1);
		return nextMonth;
	});
	const [startTime, setStartTime] = useState({ hour: 8, minute: 0, ampm: 'PM' });
	const [endTime, setEndTime] = useState({ hour: 4, minute: 0, ampm: 'AM' });
	const { isOpen: isCalendarOpen, onOpen: onCalendarOpen, onClose: onCalendarClose } = useDisclosure();
	const [selectingStart, setSelectingStart] = useState(true); // true = sélection début, false = sélection fin

	// Mapping des régions vers leurs villes
	const regionsVilles = React.useMemo(() => ({
		'Casablanca – Settat': [
			'Casablanca', 'Mohammedia', 'El Jadida', 'Settat', 'Berrechid', 'Benslimane',
			'Sidi Bennour', 'Azemmour', 'Bouznika', 'Ben Ahmed', 'Bouskoura', 'Zemamra',
			'El Gara', 'Mediouna', 'Dar Bouazza'
		],
		'Tanger – Tétouan – Al Hoceima': [
			'Tanger', 'Tétouan', 'Al Hoceima', 'Chefchaouen', 'Larache', 'Ksar El Kebir',
			'Assilah', 'Martil', 'M\'diq', 'Fnideq', 'Ouezzane'
		],
		'L\'Oriental': [
			'Oujda', 'Nador', 'Berkane', 'Taourirt', 'Jerada', 'Guercif', 'Driouch',
			'Figuig', 'Beni Ansar', 'Al Aroui'
		],
		'Fès – Meknès': [
			'Fès', 'Meknès', 'Ifrane', 'El Hajeb', 'Sefrou', 'Boulemane', 'Taza',
			'Taounate', 'Moulay Idriss Zerhoun', 'Missour'
		],
		'Rabat – Salé – Kénitra': [
			'Rabat', 'Salé', 'Kénitra', 'Skhirat', 'Témara', 'Tiflet', 'Sidi Slimane',
			'Sidi Kacem', 'Mehdia', 'Souk El Arbaa'
		],
		'Béni Mellal – Khénifra': [
			'Béni Mellal', 'Khénifra', 'Azilal', 'Khouribga', 'Fkih Ben Salah',
			'Oued Zem', 'Kasba Tadla'
		],
		'Marrakech – Safi': [
			'Marrakech', 'Safi', 'Chichaoua', 'El Kelaâ des Sraghna', 'Essaouira',
			'Youssoufia', 'Rehamna', 'Benguérir', 'Tamanar'
		],
		'Drâa – Tafilalet': [
			'Errachidia', 'Ouarzazate', 'Tinghir', 'Zagora', 'Midelt', 'Rissani',
			'Erfoud', 'Boumalne Dades'
		],
		'Guelmim – Oued Noun': [
			'Guelmim', 'Tan-Tan', 'Sidi Ifni', 'Assa', 'Lakhsas'
		],
		'Souss – Massa': [
			'Agadir', 'Inezgane', 'Aït Melloul', 'Taroudant', 'Tiznit', 'Oulad Teima',
			'Tata', 'Biougra', 'Aït Baha'
		],
		'Laâyoune – Sakia El Hamra': [
			'Laâyoune', 'Es-Semara', 'Boujdour', 'Tarfaya', 'El Marsa'
		],
		'Dakhla – Oued Ed-Dahab': [
			'Dakhla', 'Aousserd', 'Bir Anzarane'
		]
	}), []);

	// Liste complète de toutes les villes de toutes les régions
	const toutesLesVilles = React.useMemo(() => {
		const villes = [];
		Object.values(regionsVilles).forEach(villesRegion => {
			villes.push(...villesRegion);
		});
		// Trier les villes par ordre alphabétique
		return villes.sort();
	}, [regionsVilles]);

	// Filtrer les villes selon la région sélectionnée (sans tri alphabétique pour garder l'ordre original)
	const villesDisponibles = React.useMemo(() => {
		if (selectedRegion === 'tous' || !selectedRegion) {
			return toutesLesVilles;
		}
		const villesRegion = regionsVilles[selectedRegion] || [];
		return villesRegion; // Garder l'ordre original, sans tri alphabétique
	}, [selectedRegion, toutesLesVilles, regionsVilles]);

	// Sélectionner automatiquement la ville principale (première ville) de la région sélectionnée
	React.useEffect(() => {
		if (selectedRegion && selectedRegion !== 'tous') {
			const villesRegion = regionsVilles[selectedRegion] || [];
			if (villesRegion.length > 0) {
				// Sélectionner automatiquement la première ville (ville principale) de la région
				const villePrincipale = villesRegion[0];
				setSelectedVille(villePrincipale);
			}
		} else if (selectedRegion === 'tous') {
			// Si "Toutes" est sélectionné, réinitialiser la ville à "Toutes"
			setSelectedVille('tous');
		}
	}, [selectedRegion, regionsVilles, setSelectedVille]);


	// Fonction pour gérer le clic sur une date
	const handleDateClick = (date, isFirstCalendar) => {
		if (isFirstCalendar) {
			// Calendrier de gauche = date de début
			if (dateRange[1] && date > dateRange[1]) {
				// Si la date de début est après la date de fin, réinitialiser
				setDateRange([date, null]);
				setSelectingStart(false);
			} else {
				setDateRange([date, dateRange[1]]);
				if (dateRange[1]) {
					setSelectedPeriod('custom');
				}
			}
		} else {
			// Calendrier de droite = date de fin
			if (!dateRange[0]) {
				// Si pas de date de début, définir comme date de début
				setDateRange([date, null]);
			} else if (date < dateRange[0]) {
				// Si la date de fin est avant la date de début, inverser
				setDateRange([date, dateRange[0]]);
				setSelectedPeriod('custom');
			} else {
				setDateRange([dateRange[0], date]);
				setSelectedPeriod('custom');
			}
		}
	};

	// Fonction pour gérer l'ouverture du calendrier
	const handleCalendarOpen = () => {
		onCalendarOpen();
		setSelectedPeriod('custom');
		setSelectingStart(true);
		// Si une date de début est sélectionnée, initialiser le deuxième calendrier au mois suivant
		if (dateRange && dateRange[0] && dateRange[0] instanceof Date) {
			const startDate = dateRange[0];
			const nextMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1);
			setCalendarMonth2(nextMonth);
		} else if (calendarMonth && calendarMonth instanceof Date) {
			// Sinon, initialiser au mois suivant du premier calendrier
			const nextMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1);
			setCalendarMonth2(nextMonth);
		} else {
			// Par défaut, initialiser au mois suivant de la date actuelle
			const now = new Date();
			const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
			setCalendarMonth2(nextMonth);
		}
	};

	// Fonction pour obtenir le breadcrumb dynamique
	const getBreadcrumb = () => {
		const pathname = location.pathname;
		// Décoder le pathname pour gérer les caractères encodés
		const decodedPathname = decodeURIComponent(pathname);

		// Vérifier avec le pathname décodé
		const isAdherentPageCheck = decodedPathname.includes('/adhérents') || pathname.includes('/adh%C3%A9rents');

		// Trouver la route correspondante
		const findRoute = (routesList, pathnameToCheck) => {
			const decodedPathnameToCheck = decodeURIComponent(pathnameToCheck);
			for (let route of routesList) {
				if (route.layout && route.path) {
					const fullPath = route.layout + route.path;
					const decodedFullPath = decodeURIComponent(fullPath);
					if (pathnameToCheck === fullPath || pathnameToCheck.startsWith(fullPath + '/') ||
						decodedPathnameToCheck === decodedFullPath || decodedPathnameToCheck.startsWith(decodedFullPath + '/')) {
						return route;
					}
				}
				if (route.items) {
					const found = findRoute(route.items, pathnameToCheck);
					if (found) return found;
				}
			}
			return null;
		};

		const currentRoute = findRoute(routes, pathname);

		// Déterminer le parent du breadcrumb et le nom de la page actuelle
		let parent = 'Accueil';
		let currentPage = 'Page';

		// Pages de gestion des ressources
		if (decodedPathname.includes('/vehicules') || decodedPathname.includes('/materiels') || decodedPathname.includes('/utilisateurs')) {
			parent = 'Gestion des ressources';
			if (decodedPathname.includes('/utilisateurs')) {
				currentPage = 'Utilisateurs';
			} else if (decodedPathname.includes('/materiels')) {
				currentPage = 'Matériels';
			} else if (decodedPathname.includes('/vehicules')) {
				currentPage = 'Ambulances';
			}
		}

		// Pages opérationnelles (Gestion de mission)
		else if (pathname.includes('/missions') || pathname.includes('/avis') || pathname.includes('/carte-gps')) {
			parent = 'Gestion de mission';
			if (pathname.includes('/missions')) {
				currentPage = 'Missions';
			} else if (pathname.includes('/avis')) {
				currentPage = 'Avis';
			} else if (pathname.includes('/carte-gps')) {
				currentPage = 'Carte GPS';
			}
		}
		// Pages Analytics
		else if (pathname.includes('/analyses')) {
			parent = 'Analytics';
			currentPage = 'Analyses et Reporting';
		}
		else if (pathname.includes('/default')) {
			parent = 'Accueil';
			currentPage = 'Tableau de bord';
		}
		// Pages Sécurité et accès
		else if (pathname.includes('/profile') || pathname.includes('/coming-soon')) {
			parent = 'Sécurité et accès';
			if (pathname.includes('/profile')) {
				currentPage = 'Paramètres';
			} else if (pathname.includes('/coming-soon')) {
				currentPage = 'Rôles et Permissions';
			}
		}
		// Pages wallet
		else if (pathname.includes('/wallet')) {
			parent = 'Gestion financière';
			currentPage = 'Wallet';
		}
		// Autres pages - utiliser le nom de la route ou brandText en dernier recours
		else {
			parent = 'Accueil';
			currentPage = currentRoute?.name || brandText || 'Page';
		}

		return { parent, currentPage };
	};

	const breadcrumb = getBreadcrumb();

	useEffect(() => {
		window.addEventListener('scroll', changeNavbar);

		return () => {
			window.removeEventListener('scroll', changeNavbar);
		};
	});

	// Mesurer la hauteur de la navbar et la mettre à jour dynamiquement
	useEffect(() => {
		const updateNavbarHeight = () => {
			if (navbarRef.current) {
				// Utiliser requestAnimationFrame pour s'assurer que le DOM est mis à jour
				requestAnimationFrame(() => {
					if (navbarRef.current) {
						const height = navbarRef.current.offsetHeight;
						// Pour un élément fixed, on doit obtenir le top depuis les styles calculés
						const computedStyle = window.getComputedStyle(navbarRef.current);
						const topValue = parseFloat(computedStyle.top) || 0;
						// Calculer la hauteur totale : hauteur de la navbar + position top + marge supplémentaire
						const totalHeight = height + topValue + 4; // 4px de marge supplémentaire pour un espacement minimal
						setNavbarHeight(totalHeight);
					}
				});
			}
		};

		// Mettre à jour immédiatement
		updateNavbarHeight();

		// Mettre à jour quand les stats changent ou après un délai pour laisser le DOM se mettre à jour
		const timeoutId = setTimeout(updateNavbarHeight, 100);
		const timeoutId2 = setTimeout(updateNavbarHeight, 300);
		const timeoutId3 = setTimeout(updateNavbarHeight, 500);

		// Observer les changements de taille
		const resizeObserver = new ResizeObserver(() => {
			updateNavbarHeight();
		});

		// Observer les changements dans le DOM
		const mutationObserver = new MutationObserver(() => {
			updateNavbarHeight();
		});

		if (navbarRef.current) {
			resizeObserver.observe(navbarRef.current);
			mutationObserver.observe(navbarRef.current, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ['style', 'class']
			});
		}

		// Écouter les changements de taille de fenêtre
		window.addEventListener('resize', updateNavbarHeight);

		return () => {
			clearTimeout(timeoutId);
			clearTimeout(timeoutId2);
			clearTimeout(timeoutId3);
			resizeObserver.disconnect();
			mutationObserver.disconnect();
			window.removeEventListener('resize', updateNavbarHeight);
		};
	}, [showStats, stats, setNavbarHeight]);

	// Here are all the props that may change depending on navbar's type or state.(variant, scrolled)
	let navbarPosition = 'fixed';
	let secondaryMargin = '0px';
	const changeNavbar = () => {
		if (window.scrollY > 1) {
			setScrolled(true);
		} else {
			setScrolled(false);
		}
	};

	return (
		<Box
			ref={navbarRef}
			position={navbarPosition}
			display="flex"
			flexDirection="column"
			alignItems="flex-start"
			bg="white"
			border="1px solid #E5E5E5"
			borderRadius="12px"
			mx='auto'
			mt={secondaryMargin}
			right={{ base: '12px', md: '30px', lg: '30px', xl: '30px' }}
			top={{ base: '12px', md: '16px', lg: '20px', xl: '20px' }}
			w={{
				base: 'calc(100vw - 6%)',
				md: 'calc(100vw - 8%)',
				lg: 'calc(100vw - 6%)',
				xl: 'calc(100vw - 300px)',
				'2xl': 'calc(100vw - 300px)'
			}}
			zIndex={100}
			overflow="visible">
			{/* Section supérieure - Header */}
			<Flex
				w="100%"
				alignItems="center"
				px="20px"
				py="10px"
				borderBottom="1px solid #E5E5E5"
				borderRadius="12px 12px 0 0">
				{/* Breadcrumb à gauche */}
				<Flex flex="1" gap="10px" alignItems="center">
					<Button
						variant="ghost"
						size="sm"
						minW="36px"
						h="36px"
						p="5px"
						borderRadius="6px"
						boxShadow="0px 3px 4px 0px rgba(0,0,0,0.03)"
						display="flex"
						alignItems="center"
						justifyContent="center">
						<Icon as={MdDashboard} w="16px" h="16px" color="#656C7A" />
					</Button>
					<Text fontSize="12px" fontWeight="500" color="#656C7A" fontFamily="Poppins, sans-serif">
						{breadcrumb.parent}
					</Text>
					<Icon as={IoMdArrowForward} w="12px" h="12px" color="#656C7A" />
					<Text fontSize="12px" fontWeight="500" color="#455879" fontFamily="Poppins, sans-serif">
						{breadcrumb.currentPage}
					</Text>
				</Flex>

				{/* Icônes à droite */}
				<Flex gap="10px" alignItems="center">
					<AdminNavbarLinks
						onOpen={props.onOpen}
						logoText={props.logoText}
						secondary={props.secondary}
						fixed={props.fixed}
						scrolled={scrolled}
					/>
				</Flex>
			</Flex>

			{/* Section inférieure - Header secondaire */}
			<Flex
				w="100%"
				alignItems="center"
				px="20px"
				py="14px"
				borderTop="none"
				borderBottom="1px solid #E5E5E5"
				borderLeft="1px solid #E5E5E5"
				borderRight="1px solid #E5E5E5"
				borderRadius="0 0 12px 12px">
				{/* Titre avec icône à gauche */}
				<Flex flex="1" gap="10px" alignItems="center">
					{isProspectsPage ? (
						<>
							<Box
								bg="blue.50"
								border="1px solid rgba(69,156,233,0.2)"
								borderRadius="6px"
								w="36px"
								h="36px"
								display="flex"
								alignItems="center"
								justifyContent="center"
								p="10px 9px">
								<Icon as={MdPeople} w="16px" h="16px" color="#006133" />
							</Box>
							<Text
								fontSize="16px"
								fontWeight="500"
								color="#455879"
								fontFamily="Poppins, sans-serif"
								lineHeight="14px">
								Prospects
							</Text>
						</>
					) : isAdherentPage ? (
						<>
							<Box
								bg="blue.50"
								border="1px solid rgba(69,156,233,0.2)"
								borderRadius="6px"
								w="36px"
								h="36px"
								display="flex"
								alignItems="center"
								justifyContent="center"
								p="10px 9px">
								<Icon as={MdPeople} w="16px" h="16px" color="#006133" />
							</Box>
							<Text
								fontSize="16px"
								fontWeight="500"
								color="#455879"
								fontFamily="Poppins, sans-serif"
								lineHeight="14px">
								Les Adhérents
							</Text>
						</>
					) : isPatientsPage ? (
						<>
							<Box
								bg="blue.50"
								border="1px solid rgba(69,156,233,0.2)"
								borderRadius="6px"
								w="36px"
								h="36px"
								display="flex"
								alignItems="center"
								justifyContent="center"
								p="10px 9px">
								<Icon as={MdPeople} w="16px" h="16px" color="#006133" />
							</Box>
							<Text
								fontSize="16px"
								fontWeight="500"
								color="#455879"
								fontFamily="Poppins, sans-serif"
								lineHeight="14px">
								Patients
							</Text>
						</>
					) : isClientsPage ? (
						<>
							<Box
								bg="blue.50"
								border="1px solid rgba(69,156,233,0.2)"
								borderRadius="6px"
								w="36px"
								h="36px"
								display="flex"
								alignItems="center"
								justifyContent="center"
								p="10px 9px">
								<Icon as={MdBusiness} w="16px" h="16px" color="#006133" />
							</Box>
							<Text
								fontSize="16px"
								fontWeight="500"
								color="#455879"
								fontFamily="Poppins, sans-serif"
								lineHeight="14px">
								Clients
							</Text>
						</>
					) : isUtilisateursPage ? (
						<>
							<Box
								bg="blue.50"
								border="1px solid rgba(69,156,233,0.2)"
								borderRadius="6px"
								w="36px"
								h="36px"
								display="flex"
								alignItems="center"
								justifyContent="center"
								p="10px 9px">
								<Icon as={MdWork} w="16px" h="16px" color="#006133" />
							</Box>
							<Text
								fontSize="16px"
								fontWeight="500"
								color="#455879"
								fontFamily="Poppins, sans-serif"
								lineHeight="14px">
								Utilisateurs
							</Text>
						</>
					) : isMaterielsPage ? (
						<>
							<Box
								bg="blue.50"
								border="1px solid rgba(69,156,233,0.2)"
								borderRadius="6px"
								w="36px"
								h="36px"
								display="flex"
								alignItems="center"
								justifyContent="center"
								p="10px 9px">
								<Icon as={MdMedicalServices} w="16px" h="16px" color="#006133" />
							</Box>
							<Text
								fontSize="16px"
								fontWeight="500"
								color="#455879"
								fontFamily="Poppins, sans-serif"
								lineHeight="14px">
								Matériels
							</Text>
						</>
					) : isVehiculesPage ? (
						<>
							<Box
								bg="blue.50"
								border="1px solid rgba(69,156,233,0.2)"
								borderRadius="6px"
								w="36px"
								h="36px"
								display="flex"
								alignItems="center"
								justifyContent="center"
								p="10px 9px">
								<Icon as={MdDirectionsCar} w="16px" h="16px" color="#006133" />
							</Box>
							<Text
								fontSize="16px"
								fontWeight="500"
								color="#455879"
								fontFamily="Poppins, sans-serif"
								lineHeight="14px">
								Ambulances
							</Text>
						</>
					) : isAnalysesPage ? (
						<>
							<Box
								bg="#EFF6FF"
								border="1px solid rgba(69,156,233,0.2)"
								borderRadius="6px"
								w="36px"
								h="36px"
								display="flex"
								alignItems="center"
								justifyContent="center"
								p="10px 9px">
								<Icon as={MdBarChart} w="16px" h="16px" color="#006133" />
							</Box>
							<Text
								fontSize="16px"
								fontWeight="500"
								color="#171D28"
								fontFamily="Poppins, sans-serif"
								lineHeight="1.2">
								Analyses
							</Text>
						</>
					) : isMissionsPage ? (
						<>
							<Box
								bg="blue.50"
								border="1px solid rgba(69,156,233,0.2)"
								borderRadius="6px"
								w="36px"
								h="36px"
								display="flex"
								alignItems="center"
								justifyContent="center"
								p="10px 9px">
								<Icon as={MdWork} w="16px" h="16px" color="#006133" />
							</Box>
							<Text
								fontSize="16px"
								fontWeight="500"
								color="#455879"
								fontFamily="Poppins, sans-serif"
								lineHeight="14px">
								Missions
							</Text>
						</>
					) : isProfilPage ? (
						<>
							<Box
								bg="blue.50"
								border="1px solid rgba(69,156,233,0.2)"
								borderRadius="6px"
								w="36px"
								h="36px"
								display="flex"
								alignItems="center"
								justifyContent="center"
								p="10px 9px">
								<Icon as={MdPerson} w="16px" h="16px" color="#006133" />
							</Box>
							<Text
								fontSize="16px"
								fontWeight="500"
								color="#455879"
								fontFamily="Poppins, sans-serif"
								lineHeight="14px">
								Paramètres
							</Text>
						</>
					) : (
						<>
							<Box
								bg="#EFF6FF"
								border="1px solid rgba(69,156,233,0.2)"
								borderRadius="6px"
								w="36px"
								h="36px"
								display="flex"
								alignItems="center"
								justifyContent="center"
								p="10px 9px">
								<Icon as={FaTrophy} w="16px" h="16px" color="#006133" />
							</Box>
							<Text
								flex="1"
								fontSize="16px"
								fontWeight="500"
								color="#455879"
								fontFamily="Poppins, sans-serif"
								lineHeight="1.2">
								{brandText}
							</Text>
						</>
					)}
				</Flex>

				{/* Actions à droite */}
				<Flex
					gap={{ base: '8px', md: '14px' }}
					alignItems={{ base: 'flex-start', md: 'center' }}
					flexWrap={{ base: 'wrap', md: 'nowrap' }}
					justifyContent={{ base: 'flex-start', md: 'flex-end' }}
				>
					{isAnalysesPage ? (
						<>
							{/* Filtres pour la page Analyses */}
							<HStack
								spacing={{ base: '8px', md: '14px' }}
								flexWrap={{ base: 'wrap', md: 'nowrap' }}
								justifyContent={{ base: 'flex-start', md: 'flex-end' }}
							>
								{/* Période couverte - Boutons Jour/Semaine/Mois + Calendrier */}
								<Box
									bg="#F9F9F9"
									border="1px solid #E5E5E5"
									borderRadius="8px"
									p="3px"
									display="flex"
									gap="10px"
									alignItems="center"
									w={{ base: '100%', md: '350px' }}
								>
									<Flex gap="5px" alignItems="center">
										<Button
											h="30px"
											px="10px"
											py={selectedPeriod === 'jour' && !isCalendarOpen && selectedPeriod !== 'custom' ? '2px' : '3px'}
											fontSize="12px"
											fontWeight="500"
											fontFamily="Poppins, sans-serif"
											color={selectedPeriod === 'jour' && !isCalendarOpen && selectedPeriod !== 'custom' ? 'white' : '#171D28'}
											bg={selectedPeriod === 'jour' && !isCalendarOpen && selectedPeriod !== 'custom' ? '#006133' : 'transparent'}
											borderRadius={selectedPeriod === 'jour' && !isCalendarOpen && selectedPeriod !== 'custom' ? '8px' : '6px'}
											boxShadow={selectedPeriod === 'jour' && !isCalendarOpen && selectedPeriod !== 'custom' ? '0px 3px 4px 0px rgba(0,0,0,0.03)' : 'none'}
											onClick={() => {
												setSelectedPeriod('jour');
												setDateRange([null, null]);
												setSelectingStart(true);
												onCalendarClose();
											}}
											_hover={{ bg: selectedPeriod === 'jour' && !isCalendarOpen && selectedPeriod !== 'custom' ? '#006133' : 'transparent' }}
											_active={{ bg: selectedPeriod === 'jour' && !isCalendarOpen && selectedPeriod !== 'custom' ? '#006133' : 'transparent' }}
											cursor="pointer"
										>
											Jour
										</Button>
										<Button
											h="30px"
											px="10px"
											py={selectedPeriod === 'semaine' && !isCalendarOpen && selectedPeriod !== 'custom' ? '2px' : '3px'}
											fontSize="12px"
											fontWeight="500"
											fontFamily="Poppins, sans-serif"
											color={selectedPeriod === 'semaine' && !isCalendarOpen && selectedPeriod !== 'custom' ? 'white' : '#171D28'}
											bg={selectedPeriod === 'semaine' && !isCalendarOpen && selectedPeriod !== 'custom' ? '#006133' : 'transparent'}
											borderRadius={selectedPeriod === 'semaine' && !isCalendarOpen && selectedPeriod !== 'custom' ? '8px' : '6px'}
											boxShadow={selectedPeriod === 'semaine' && !isCalendarOpen && selectedPeriod !== 'custom' ? '0px 3px 4px 0px rgba(0,0,0,0.03)' : 'none'}
											onClick={() => {
												setSelectedPeriod('semaine');
												setDateRange([null, null]);
												setSelectingStart(true);
												onCalendarClose();
											}}
											_hover={{ bg: selectedPeriod === 'semaine' && !isCalendarOpen && selectedPeriod !== 'custom' ? '#006133' : 'transparent' }}
											_active={{ bg: selectedPeriod === 'semaine' && !isCalendarOpen && selectedPeriod !== 'custom' ? '#006133' : 'transparent' }}
											cursor="pointer"
										>
											Semaine
										</Button>
										<Button
											h="30px"
											px="10px"
											py={selectedPeriod === 'mois' && !isCalendarOpen && selectedPeriod !== 'custom' ? '2px' : '3px'}
											fontSize="12px"
											fontWeight="500"
											fontFamily="Poppins, sans-serif"
											color={selectedPeriod === 'mois' && !isCalendarOpen && selectedPeriod !== 'custom' ? 'white' : '#171D28'}
											bg={selectedPeriod === 'mois' && !isCalendarOpen && selectedPeriod !== 'custom' ? '#006133' : 'transparent'}
											borderRadius={selectedPeriod === 'mois' && !isCalendarOpen && selectedPeriod !== 'custom' ? '8px' : '6px'}
											boxShadow={selectedPeriod === 'mois' && !isCalendarOpen && selectedPeriod !== 'custom' ? '0px 3px 4px 0px rgba(0,0,0,0.03)' : 'none'}
											onClick={() => {
												setSelectedPeriod('mois');
												setDateRange([null, null]);
												setSelectingStart(true);
												onCalendarClose();
											}}
											_hover={{ bg: selectedPeriod === 'mois' && !isCalendarOpen && selectedPeriod !== 'custom' ? '#006133' : 'transparent' }}
											_active={{ bg: selectedPeriod === 'mois' && !isCalendarOpen && selectedPeriod !== 'custom' ? '#006133' : 'transparent' }}
											cursor="pointer"
										>
											Mois
										</Button>
									</Flex>

									{/* Séparateur vertical */}
									<Box w="0px" h="18px" borderLeft="1px solid #E5E5E5" />

									{/* Bouton Période couverte avec calendrier */}
									<Popover
										isOpen={isCalendarOpen}
										onOpen={handleCalendarOpen}
										onClose={onCalendarClose}
										placement="bottom-start">
										<PopoverTrigger>
											<Button
												bg={isCalendarOpen || selectedPeriod === 'custom' ? '#006133' : 'white'}
												border="none"
												borderRadius="6px"
												h="30px"
												px="10px"
												py="3px"
												maxWidth="150px"
												fontSize="12px"
												fontWeight="500"
												fontFamily="Poppins, sans-serif"
												color={isCalendarOpen || selectedPeriod === 'custom' ? 'white' : '#171D28'}
												flex="1"
												leftIcon={<Icon as={MdCalendarToday} w="16px" h="16px" />}
												boxShadow={isCalendarOpen || selectedPeriod === 'custom' ? '0px 3px 4px 0px rgba(0,0,0,0.03)' : 'none'}
												_hover={{ bg: isCalendarOpen || selectedPeriod === 'custom' ? '#006133' : 'white' }}
												_active={{ bg: isCalendarOpen || selectedPeriod === 'custom' ? '#006133' : 'white' }}
											>
												Période couverte
											</Button>
										</PopoverTrigger>
										<PopoverContent w="auto" minW="560px" p="0" borderRadius="6px" boxShadow="0px 10px 14px 0px rgba(15,42,81,0.03)" zIndex={1000}>
											<PopoverBody p="0">
												<Box bg="white" border="1px solid #E5E5E5" borderRadius="6px" overflow="hidden">
													{/* Deux calendriers côte à côte */}
													<Flex gap="0" borderBottom="1px solid #F1F1F4">
														{/* Calendrier gauche - Mois 1 */}
														<Box
															borderRight="1px solid #F1F1F4"
															borderTopLeftRadius="6px"
															borderBottomRightRadius="0"
															p="10px"
															className="analyses-date-picker"
															flex="1"
															minW="280px"
															maxW="280px"
														>
															<Calendar
																onChange={(value) => {
																	if (value instanceof Date) {
																		handleDateClick(value, true);
																	} else if (Array.isArray(value)) {
																		setDateRange(value);
																		if (value[0] && value[1]) {
																			setSelectedPeriod('custom');
																		}
																	}
																}}
																value={dateRange[0]}
																selectRange={false}
																view="month"
																activeStartDate={calendarMonth instanceof Date ? calendarMonth : new Date()}
																onActiveStartDateChange={(date) => {
																	if (date instanceof Date) {
																		setCalendarMonth(date);
																	}
																}}
																prevLabel={
																	<Box
																		as="button"
																		bg="#F9F9F9"
																		borderRadius="6px"
																		p="10px"
																		display="flex"
																		alignItems="center"
																		justifyContent="center"
																		w="30px"
																		h="30px"
																		cursor="pointer"
																		onClick={(e) => {
																			e.preventDefault();
																			e.stopPropagation();
																			const newDate = new Date(calendarMonth);
																			newDate.setMonth(newDate.getMonth() - 1);
																			setCalendarMonth(newDate);
																		}}
																		_hover={{ bg: "#F1F1F1" }}
																	>
																		<Icon as={MdChevronLeft} w="20px" h="20px" />
																	</Box>
																}
																nextLabel={
																	<Box
																		as="button"
																		bg="#F9F9F9"
																		borderRadius="6px"
																		p="10px"
																		display="flex"
																		alignItems="center"
																		justifyContent="center"
																		w="30px"
																		h="30px"
																		cursor="pointer"
																		onClick={(e) => {
																			e.preventDefault();
																			e.stopPropagation();
																			const newDate = new Date(calendarMonth);
																			newDate.setMonth(newDate.getMonth() + 1);
																			setCalendarMonth(newDate);
																		}}
																		_hover={{ bg: "#F1F1F1" }}
																	>
																		<Icon as={MdChevronRight} w="20px" h="20px" />
																	</Box>
																}
																prev2Label={null}
																next2Label={null}
																locale="fr-FR"
																tileClassName={({ date, view }) => {
																	if (view === 'month') {
																		const dateStr = date.toDateString();
																		if (dateRange[0] && dateStr === dateRange[0].toDateString()) {
																			return 'react-calendar__tile--active react-calendar__tile--rangeStart';
																		}
																		if (dateRange[0] && dateRange[1]) {
																			const dateTime = date.getTime();
																			const startTime = dateRange[0].getTime();
																			const endTime = dateRange[1].getTime();
																			if (dateTime >= startTime && dateTime <= endTime) {
																				if (dateStr === dateRange[1].toDateString()) {
																					return 'react-calendar__tile--active react-calendar__tile--rangeEnd';
																				}
																				return 'react-calendar__tile--range';
																			}
																		}
																	}
																	return null;
																}}
																style={{
																	border: 'none',
																	fontFamily: 'Inter, sans-serif',
																}}
															/>
															{/* Sélecteur de temps pour la date de début */}
															<Flex justify="space-between" align="center" px="40px" py="12px" borderTop="1px solid #F1F1F4">
																<Menu>
																	<MenuButton
																		as={Button}
																		rightIcon={<Icon as={MdArrowDropDown} w="16px" h="16px" />}
																		bg="transparent"
																		border="none"
																		p="0"
																		h="auto"
																		fontSize="13px"
																		fontWeight="500"
																		fontFamily="Inter, sans-serif"
																		color="#656C7A"
																		letterSpacing="-0.13px"
																		_hover={{ bg: "transparent" }}
																	>
																		{startTime.hour}
																	</MenuButton>
																	<MenuList>
																		{[...Array(12)].map((_, i) => (
																			<MenuItem key={i} onClick={() => setStartTime({ ...startTime, hour: i + 1 })}>
																				{i + 1}
																			</MenuItem>
																		))}
																	</MenuList>
																</Menu>
																<Text fontSize="13px" fontWeight="500" color="#656C7A" letterSpacing="-0.13px">:</Text>
																<Menu>
																	<MenuButton
																		as={Button}
																		rightIcon={<Icon as={MdArrowDropDown} w="16px" h="16px" />}
																		bg="transparent"
																		border="none"
																		p="0"
																		h="auto"
																		fontSize="13px"
																		fontWeight="500"
																		fontFamily="Inter, sans-serif"
																		color="#656C7A"
																		letterSpacing="-0.13px"
																		_hover={{ bg: "transparent" }}
																	>
																		{startTime.minute.toString().padStart(2, '0')}
																	</MenuButton>
																	<MenuList>
																		<MenuItem onClick={() => setStartTime({ ...startTime, minute: 0 })}>00</MenuItem>
																		<MenuItem onClick={() => setStartTime({ ...startTime, minute: 15 })}>15</MenuItem>
																		<MenuItem onClick={() => setStartTime({ ...startTime, minute: 30 })}>30</MenuItem>
																		<MenuItem onClick={() => setStartTime({ ...startTime, minute: 45 })}>45</MenuItem>
																	</MenuList>
																</Menu>
																<Menu>
																	<MenuButton
																		as={Button}
																		rightIcon={<Icon as={MdArrowDropDown} w="16px" h="16px" />}
																		bg="transparent"
																		border="none"
																		p="0"
																		h="auto"
																		fontSize="13px"
																		fontWeight="500"
																		fontFamily="Inter, sans-serif"
																		color="#656C7A"
																		letterSpacing="-0.13px"
																		_hover={{ bg: "transparent" }}
																	>
																		{startTime.ampm}
																	</MenuButton>
																	<MenuList>
																		<MenuItem onClick={() => setStartTime({ ...startTime, ampm: 'AM' })}>AM</MenuItem>
																		<MenuItem onClick={() => setStartTime({ ...startTime, ampm: 'PM' })}>PM</MenuItem>
																	</MenuList>
																</Menu>
															</Flex>
														</Box>

														{/* Calendrier droit - Mois 2 */}
														<Box
															borderTopRightRadius="6px"
															borderBottomLeftRadius="0"
															p="10px"
															className="analyses-date-picker"
															flex="1"
															minW="280px"
															maxW="280px"
														>
															<Calendar
																onChange={(value) => {
																	if (value instanceof Date) {
																		handleDateClick(value, false);
																	} else if (Array.isArray(value)) {
																		setDateRange(value);
																		if (value[0] && value[1]) {
																			setSelectedPeriod('custom');
																		}
																	}
																}}
																value={dateRange[1]}
																selectRange={false}
																view="month"
																activeStartDate={calendarMonth2 instanceof Date ? calendarMonth2 : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)}
																onActiveStartDateChange={(date) => {
																	// Navigation indépendante pour le deuxième calendrier
																	if (date instanceof Date) {
																		setCalendarMonth2(date);
																	}
																}}
																prevLabel={
																	<Box
																		as="button"
																		bg="#F9F9F9"
																		borderRadius="6px"
																		p="10px"
																		display="flex"
																		alignItems="center"
																		justifyContent="center"
																		w="30px"
																		h="30px"
																		cursor="pointer"
																		onClick={(e) => {
																			e.preventDefault();
																			e.stopPropagation();
																			const newDate = new Date(calendarMonth2);
																			newDate.setMonth(newDate.getMonth() - 1);
																			setCalendarMonth2(newDate);
																		}}
																		_hover={{ bg: "#F1F1F1" }}
																	>
																		<Icon as={MdChevronLeft} w="20px" h="20px" />
																	</Box>
																}
																nextLabel={
																	<Box
																		as="button"
																		bg="#F9F9F9"
																		borderRadius="6px"
																		p="10px"
																		display="flex"
																		alignItems="center"
																		justifyContent="center"
																		w="30px"
																		h="30px"
																		cursor="pointer"
																		onClick={(e) => {
																			e.preventDefault();
																			e.stopPropagation();
																			const newDate = new Date(calendarMonth2);
																			newDate.setMonth(newDate.getMonth() + 1);
																			setCalendarMonth2(newDate);
																		}}
																		_hover={{ bg: "#F1F1F1" }}
																	>
																		<Icon as={MdChevronRight} w="20px" h="20px" />
																	</Box>
																}
																prev2Label={null}
																next2Label={null}
																locale="fr-FR"
																tileClassName={({ date, view }) => {
																	if (view === 'month') {
																		const dateStr = date.toDateString();
																		if (dateRange[1] && dateStr === dateRange[1].toDateString()) {
																			return 'react-calendar__tile--active react-calendar__tile--rangeEnd';
																		}
																		if (dateRange[0] && dateRange[1]) {
																			const dateTime = date.getTime();
																			const startTime = dateRange[0].getTime();
																			const endTime = dateRange[1].getTime();
																			if (dateTime >= startTime && dateTime <= endTime) {
																				if (dateStr === dateRange[0].toDateString()) {
																					return 'react-calendar__tile--active react-calendar__tile--rangeStart';
																				}
																				return 'react-calendar__tile--range';
																			}
																		}
																	}
																	return null;
																}}
																style={{
																	border: 'none',
																	fontFamily: 'Inter, sans-serif',
																}}
															/>
															{/* Sélecteur de temps pour la date de fin */}
															<Flex justify="space-between" align="center" px="40px" py="12px" borderTop="1px solid #F1F1F4">
																<Menu>
																	<MenuButton
																		as={Button}
																		rightIcon={<Icon as={MdArrowDropDown} w="16px" h="16px" />}
																		bg="transparent"
																		border="none"
																		p="0"
																		h="auto"
																		fontSize="13px"
																		fontWeight="500"
																		fontFamily="Inter, sans-serif"
																		color="#656C7A"
																		letterSpacing="-0.13px"
																		_hover={{ bg: "transparent" }}
																	>
																		{endTime.hour}
																	</MenuButton>
																	<MenuList>
																		{[...Array(12)].map((_, i) => (
																			<MenuItem key={i} onClick={() => setEndTime({ ...endTime, hour: i + 1 })}>
																				{i + 1}
																			</MenuItem>
																		))}
																	</MenuList>
																</Menu>
																<Text fontSize="13px" fontWeight="500" color="#656C7A" letterSpacing="-0.13px">:</Text>
																<Menu>
																	<MenuButton
																		as={Button}
																		rightIcon={<Icon as={MdArrowDropDown} w="16px" h="16px" />}
																		bg="transparent"
																		border="none"
																		p="0"
																		h="auto"
																		fontSize="13px"
																		fontWeight="500"
																		fontFamily="Inter, sans-serif"
																		color="#656C7A"
																		letterSpacing="-0.13px"
																		_hover={{ bg: "transparent" }}
																	>
																		{endTime.minute.toString().padStart(2, '0')}
																	</MenuButton>
																	<MenuList>
																		<MenuItem onClick={() => setEndTime({ ...endTime, minute: 0 })}>00</MenuItem>
																		<MenuItem onClick={() => setEndTime({ ...endTime, minute: 15 })}>15</MenuItem>
																		<MenuItem onClick={() => setEndTime({ ...endTime, minute: 30 })}>30</MenuItem>
																		<MenuItem onClick={() => setEndTime({ ...endTime, minute: 45 })}>45</MenuItem>
																	</MenuList>
																</Menu>
																<Menu>
																	<MenuButton
																		as={Button}
																		rightIcon={<Icon as={MdArrowDropDown} w="16px" h="16px" />}
																		bg="transparent"
																		border="none"
																		p="0"
																		h="auto"
																		fontSize="13px"
																		fontWeight="500"
																		fontFamily="Inter, sans-serif"
																		color="#656C7A"
																		letterSpacing="-0.13px"
																		_hover={{ bg: "transparent" }}
																	>
																		{endTime.ampm}
																	</MenuButton>
																	<MenuList>
																		<MenuItem onClick={() => setEndTime({ ...endTime, ampm: 'AM' })}>AM</MenuItem>
																		<MenuItem onClick={() => setEndTime({ ...endTime, ampm: 'PM' })}>PM</MenuItem>
																	</MenuList>
																</Menu>
															</Flex>
														</Box>
													</Flex>

													{/* Boutons Annuler et Appliquer */}
													<Flex justify="flex-end" p="12px" borderTop="1px solid #DBDFE9">
														<HStack spacing="10px">
															<Button
																bg="#F9F9F9"
																border="1px solid #F1F1F4"
																borderRadius="6px"
																px="10px"
																py="8px"
																fontSize="11px"
																fontWeight="500"
																fontFamily="Inter, sans-serif"
																color="#656C7A"
																onClick={onCalendarClose}
																_hover={{ bg: "#F1F1F1" }}
															>
																Annuler
															</Button>
															<Button
																bg="#006133"
																borderRadius="6px"
																px="10px"
																py="8px"
																fontSize="11px"
																fontWeight="500"
																fontFamily="Inter, sans-serif"
																color="white"
																onClick={() => {
																	if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
																		setSelectedPeriod('custom');
																	}
																	onCalendarClose();
																}}
																_hover={{ bg: "#3a8cd4" }}
															>
																Appliquer
															</Button>
														</HStack>
													</Flex>
												</Box>
											</PopoverBody>
										</PopoverContent>
									</Popover>
								</Box>

								{/* Région */}
								<Menu>
									<MenuButton
										as={Button}
										rightIcon={<Icon as={MdArrowDropDown} w="16px" h="16px" transform="rotate(90deg)" />}
										bg="white"
										border="1px solid #E5E5E5"
										borderRadius="6px"
										h="42px"
										px="14px"
										py="12px"
										fontSize="13px"
										fontWeight="400"
										fontFamily="Poppins, sans-serif"
										color="#656C7A"
										boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
										_hover={{ bg: "white" }}
										_active={{ bg: "white" }}
										w={{ base: '100%', md: '273px' }}
									>
										<Text textAlign="left" w="100%">
											Région: <Text as="span" fontWeight="500" color="#171D28">
												{selectedRegion === 'tous' || !selectedRegion ? 'Toutes' : selectedRegion}
											</Text>
										</Text>
									</MenuButton>
									<MenuList>
										<MenuItem
											onClick={() => setSelectedRegion('tous')}
											bg={selectedRegion === 'tous' ? "#EFF6FF" : "transparent"}
											borderColor={selectedRegion === 'tous' ? "rgba(69,156,233,0.2)" : "transparent"}
										>
											<Flex justify="space-between" w="100%">
												<Text>Tout sélectionner</Text>
												{selectedRegion === 'tous' && <Icon as={MdCheckCircle} w="16px" h="16px" color="#006133" />}
											</Flex>
										</MenuItem>
										{Object.keys(regionsVilles).map((region) => (
											<MenuItem
												key={region}
												onClick={() => setSelectedRegion(region)}
												bg={selectedRegion === region ? "#EFF6FF" : "transparent"}
												borderColor={selectedRegion === region ? "rgba(69,156,233,0.2)" : "transparent"}
											>
												<Flex justify="space-between" w="100%">
													<Text>{region}</Text>
													{selectedRegion === region && <Icon as={MdCheckCircle} w="16px" h="16px" color="#006133" />}
												</Flex>
											</MenuItem>
										))}
									</MenuList>
								</Menu>

								{/* Ville */}
								<Menu>
									<MenuButton
										as={Button}
										rightIcon={<Icon as={MdArrowDropDown} w="16px" h="16px" transform="rotate(90deg)" />}
										bg="white"
										border="1px solid #E5E5E5"
										borderRadius="6px"
										h="42px"
										px="14px"
										py="12px"
										fontSize="13px"
										fontWeight="400"
										fontFamily="Poppins, sans-serif"
										color="#656C7A"
										boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
										_hover={{ bg: "white" }}
										_active={{ bg: "white" }}
										w={{ base: '100%', md: '149px' }}
									>
										<Text textAlign="left" w="100%">
											Ville: <Text as="span" fontWeight="500" color="#171D28">{selectedVille === 'tous' ? 'Toutes' : selectedVille}</Text>
										</Text>
									</MenuButton>
									<MenuList maxH="400px" overflowY="auto">
										<MenuItem onClick={() => setSelectedVille('tous')}>
											<Flex justify="space-between" w="100%">
												<Text>Tout sélectionner</Text>
												{selectedVille === 'tous' && <Icon as={MdCheckCircle} w="16px" h="16px" color="#006133" />}
											</Flex>
										</MenuItem>
										{villesDisponibles.length > 0 ? (
											villesDisponibles.map((ville, index) => (
												<MenuItem
													key={index}
													onClick={() => setSelectedVille(ville)}
													bg={selectedVille === ville ? "#EFF6FF" : "transparent"}
													borderColor={selectedVille === ville ? "rgba(69,156,233,0.2)" : "transparent"}
												>
													<Flex justify="space-between" w="100%">
														<Text>{ville}</Text>
														{selectedVille === ville && <Icon as={MdCheckCircle} w="16px" h="16px" color="#006133" />}
													</Flex>
												</MenuItem>
											))
										) : (
											<MenuItem isDisabled>
												<Text fontSize="13px" color="#656C7A">Aucune ville disponible</Text>
											</MenuItem>
										)}
									</MenuList>
								</Menu>

								{/* Analyses */}
								<Menu>
									<MenuButton
										as={Button}
										rightIcon={<Icon as={MdArrowDropDown} w="16px" h="16px" transform="rotate(90deg)" />}
										bg="white"
										border="1px solid #E5E5E5"
										borderRadius="6px"
										h="42px"
										px="14px"
										py="12px"
										fontSize="13px"
										fontWeight="500"
										fontFamily="Poppins, sans-serif"
										color="#656C7A"
										boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
										_hover={{ bg: "white" }}
										_active={{ bg: "white" }}
										w={{ base: '100%', md: '200px' }}
									>
										<Text textAlign="left" w="100%">
											{selectedAnalysesLabel}
										</Text>
									</MenuButton>
									<MenuList>
										<MenuItem onClick={() => setSelectedAnalyses(['tous'])}>
											<HStack spacing="10px">
												<Box w="18px" h="18px" bg={isAllAnalysesSelected ? '#006133' : '#E5E5E5'} borderRadius="4px" display="flex" alignItems="center" justifyContent="center">
													{isAllAnalysesSelected && <Icon as={MdCheckCircle} w="16px" h="16px" color="white" />}
												</Box>
												<Text fontWeight={isAllAnalysesSelected ? '600' : '400'}>Sélectionner tous</Text>
											</HStack>
										</MenuItem>
										{analysesOptions.map((option) => {
											const isChecked = !isAllAnalysesSelected && selectedAnalysesList.includes(option.key);
											return (
												<MenuItem key={option.key} onClick={() => toggleSelectedAnalysis(option.key)}>
													<HStack spacing="10px">
														<Box w="18px" h="18px" bg={isChecked ? '#006133' : '#E5E5E5'} borderRadius="4px" display="flex" alignItems="center" justifyContent="center">
															{isChecked && <Icon as={MdCheckCircle} w="16px" h="16px" color="white" />}
														</Box>
														<Text fontWeight={isChecked ? '600' : '400'}>{option.label}</Text>
													</HStack>
												</MenuItem>
											);
										})}
									</MenuList>
								</Menu>
							</HStack>

							{/* Bouton Créer une mission */}
							{canCreateMission && (
								<Button
									leftIcon={<Img src={plusSquaredIcon} w="16px" h="16px" />}
									bg="#006133"
									color="white"
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
									_hover={{ bg: '#004f2aff' }}
									w={{ base: '100%', md: 'auto' }}
									onClick={handleCreateMissionClick}
								>
									Créer un dossier de régulation
								</Button>
							)}
						</>
					) : isProspectsPage ? (
						<>
							<HStack
								spacing="12px"
								bg="gray.50"
								px="12px"
								py="6px"
								borderRadius="12px"
								border="1px solid"
								borderColor="gray.200"
								boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
								transition="all 0.2s"
								_hover={{ borderColor: "gray.300", bg: "gray.100" }}
							>
								<Text fontSize="12px" color="gray.600" fontWeight="600" fontFamily="Poppins, sans-serif" whiteSpace="nowrap">
									Afficher les stats
								</Text>
								<Switch
									isChecked={showStats}
									onChange={(e) => setShowStats(e.target.checked)}
									size="sm"
									sx={{
										'& .chakra-switch__track': {
											w: '34px',
											h: '20px',
											bg: showStats ? '#006133' : '#E2E8F0',
											_focus: { boxShadow: 'none' },
										},
										'& .chakra-switch__thumb': {
											w: '16px',
											h: '16px',
											bg: 'white',
											boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
											_checked: {
												transform: 'translateX(14px)',
											},
										}
									}}
								/>
							</HStack>
							{onCreateProspect && hasPermission('adherents_create') && (
								<Button
									leftIcon={<MdAdd />}
									bg="#006133"
									color="white"
									onClick={onCreateProspect}
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									_hover={{ bg: '#3a8cd4' }}
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
								>
									Créer un prospect
								</Button>
							)}
						</>
					) : isAdherentPage ? (
						<>
							<HStack
								spacing="12px"
								bg="gray.50"
								px="12px"
								py="6px"
								borderRadius="12px"
								border="1px solid"
								borderColor="gray.200"
								boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
								transition="all 0.2s"
								_hover={{ borderColor: "gray.300", bg: "gray.100" }}
							>
								<Text fontSize="12px" color="gray.600" fontWeight="600" fontFamily="Poppins, sans-serif" whiteSpace="nowrap">
									Afficher les stats
								</Text>
								<Switch
									isChecked={showStats}
									onChange={(e) => setShowStats(e.target.checked)}
									size="sm"
									sx={{
										'& .chakra-switch__track': {
											w: '34px',
											h: '20px',
											bg: showStats ? '#006133' : '#E2E8F0',
											_focus: { boxShadow: 'none' },
										},
										'& .chakra-switch__thumb': {
											w: '16px',
											h: '16px',
											bg: 'white',
											boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
											_checked: {
												transform: 'translateX(14px)',
											},
										}
									}}
								/>
							</HStack>
							{onCreateAdherent && typeof onCreateAdherent === 'function' && hasPermission('adherents_create') ? (
								<Button
									leftIcon={<MdAdd />}
									bg="#006133"
									color="white"
									onClick={onCreateAdherent}
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									_hover={{ bg: '#3a8cd4' }}
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
								>
									Créer un adhérent
								</Button>
							) : null}
						</>
					) : isPatientsPage ? (
						<>
							<HStack
								spacing="12px"
								bg="gray.50"
								px="12px"
								py="6px"
								borderRadius="12px"
								border="1px solid"
								borderColor="gray.200"
								boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
								transition="all 0.2s"
								_hover={{ borderColor: "gray.300", bg: "gray.100" }}
							>
								<Text fontSize="12px" color="gray.600" fontWeight="600" fontFamily="Poppins, sans-serif" whiteSpace="nowrap">
									Afficher les stats
								</Text>
								<Switch
									isChecked={showStats}
									onChange={(e) => setShowStats(e.target.checked)}
									size="sm"
									sx={{
										'& .chakra-switch__track': {
											w: '34px',
											h: '20px',
											bg: showStats ? '#006133' : '#E2E8F0',
											_focus: { boxShadow: 'none' },
										},
										'& .chakra-switch__thumb': {
											w: '16px',
											h: '16px',
											bg: 'white',
											boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
											_checked: {
												transform: 'translateX(14px)',
											},
										}
									}}
								/>
							</HStack>
							{onCreatePatient && hasPermission('patients_create') && (
								<Button
									leftIcon={<MdAdd />}
									bg="#006133"
									color="white"
									onClick={onCreatePatient}
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									_hover={{ bg: '#3a8cd4' }}
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
								>
									Créer un patient
								</Button>
							)}
						</>
					) : isClientsPage ? (
						<>
							<HStack
								spacing="12px"
								bg="gray.50"
								px="12px"
								py="6px"
								borderRadius="12px"
								border="1px solid"
								borderColor="gray.200"
								boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
								transition="all 0.2s"
								_hover={{ borderColor: "gray.300", bg: "gray.100" }}
							>
								<Text fontSize="12px" color="gray.600" fontWeight="600" fontFamily="Poppins, sans-serif" whiteSpace="nowrap">
									Afficher les stats
								</Text>
								<Switch
									isChecked={showStats}
									onChange={(e) => setShowStats(e.target.checked)}
									size="sm"
									sx={{
										'& .chakra-switch__track': {
											w: '34px',
											h: '20px',
											bg: showStats ? '#006133' : '#E2E8F0',
											_focus: { boxShadow: 'none' },
										},
										'& .chakra-switch__thumb': {
											w: '16px',
											h: '16px',
											bg: 'white',
											boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
											_checked: {
												transform: 'translateX(14px)',
											},
										}
									}}
								/>
							</HStack>
							{onCreateClient && hasPermission('adherents_create') && (
								<Button
									leftIcon={<MdAdd />}
									bg="#006133"
									color="white"
									onClick={onCreateClient}
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									_hover={{ bg: '#3a8cd4' }}
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
								>
									Créer un client
								</Button>
							)}
						</>
					) : isUtilisateursPage ? (
						<>
							<HStack
								spacing="12px"
								bg="gray.50"
								px="12px"
								py="6px"
								borderRadius="12px"
								border="1px solid"
								borderColor="gray.200"
								boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
								transition="all 0.2s"
								_hover={{ borderColor: "gray.300", bg: "gray.100" }}
							>
								<Text fontSize="12px" color="gray.600" fontWeight="600" fontFamily="Poppins, sans-serif" whiteSpace="nowrap">
									Afficher les stats
								</Text>
								<Switch
									isChecked={showStats}
									onChange={(e) => setShowStats(e.target.checked)}
									size="sm"
									sx={{
										'& .chakra-switch__track': {
											w: '34px',
											h: '20px',
											bg: showStats ? '#006133' : '#E2E8F0',
											_focus: { boxShadow: 'none' },
										},
										'& .chakra-switch__thumb': {
											w: '16px',
											h: '16px',
											bg: 'white',
											boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
											_checked: {
												transform: 'translateX(14px)',
											},
										}
									}}
								/>
							</HStack>
							{onCreateUtilisateur && hasPermission('utilisateurs_create') && (
								<Button
									leftIcon={<MdAdd />}
									bg="#006133"
									color="white"
									onClick={onCreateUtilisateur}
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									_hover={{ bg: '#3a8cd4' }}
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
								>
									Créer un utilisateur
								</Button>
							)}
						</>
					) : isMaterielsPage ? (
						<>
							<HStack
								spacing="12px"
								bg="gray.50"
								px="12px"
								py="6px"
								borderRadius="12px"
								border="1px solid"
								borderColor="gray.200"
								boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
								transition="all 0.2s"
								_hover={{ borderColor: "gray.300", bg: "gray.100" }}
							>
								<Text fontSize="12px" color="gray.600" fontWeight="600" fontFamily="Poppins, sans-serif" whiteSpace="nowrap">
									Afficher les stats
								</Text>
								<Switch
									isChecked={showStats}
									onChange={(e) => setShowStats(e.target.checked)}
									size="sm"
									sx={{
										'& .chakra-switch__track': {
											w: '34px',
											h: '20px',
											bg: showStats ? '#006133' : '#E2E8F0',
											_focus: { boxShadow: 'none' },
										},
										'& .chakra-switch__thumb': {
											w: '16px',
											h: '16px',
											bg: 'white',
											boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
											_checked: {
												transform: 'translateX(14px)',
											},
										}
									}}
								/>
							</HStack>
							{onCreateMateriel && hasPermission('materiels_create') && (
								<Button
									leftIcon={<MdAdd />}
									bg="#006133"
									color="white"
									onClick={onCreateMateriel}
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									_hover={{ bg: '#3a8cd4' }}
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
								>
									Créer un matériel
								</Button>
							)}
						</>
					) : isVehiculesPage ? (
						<>
							<HStack
								spacing="12px"
								bg="gray.50"
								px="12px"
								py="6px"
								borderRadius="12px"
								border="1px solid"
								borderColor="gray.200"
								boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
								transition="all 0.2s"
								_hover={{ borderColor: "gray.300", bg: "gray.100" }}
							>
								<Text fontSize="12px" color="gray.600" fontWeight="600" fontFamily="Poppins, sans-serif" whiteSpace="nowrap">
									Afficher les stats
								</Text>
								<Switch
									isChecked={showStats}
									onChange={(e) => setShowStats(e.target.checked)}
									size="sm"
									sx={{
										'& .chakra-switch__track': {
											w: '34px',
											h: '20px',
											bg: showStats ? '#006133' : '#E2E8F0',
											_focus: { boxShadow: 'none' },
										},
										'& .chakra-switch__thumb': {
											w: '16px',
											h: '16px',
											bg: 'white',
											boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
											_checked: {
												transform: 'translateX(14px)',
											},
										}
									}}
								/>
							</HStack>
							{onCreateVehicule && hasPermission('vehicules_create') && (

								<Button
									leftIcon={<Img src={plusSquaredIcon} w="16px" h="16px" />}
									bg="#006133"
									color="white"
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
									_hover={{ bg: '#004f2aff' }}
									w={{ base: '100%', md: 'auto' }}
									onClick={onCreateVehicule}
								>
									Créer une Ambulance
								</Button>
							)}
						</>
					) : isMissionsPage ? (
						<>
							<HStack
								spacing="12px"
								bg="gray.50"
								px="12px"
								py="6px"
								borderRadius="12px"
								border="1px solid"
								borderColor="gray.200"
								boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
								transition="all 0.2s"
								_hover={{ borderColor: "gray.300", bg: "gray.100" }}
							>
								<Text fontSize="12px" color="gray.600" fontWeight="600" fontFamily="Poppins, sans-serif" whiteSpace="nowrap">
									Afficher les stats
								</Text>
								<Switch
									isChecked={showStats}
									onChange={(e) => setShowStats(e.target.checked)}
									size="sm"
									sx={{
										'& .chakra-switch__track': {
											w: '34px',
											h: '20px',
											bg: showStats ? '#006133' : '#E2E8F0',
											_focus: { boxShadow: 'none' },
										},
										'& .chakra-switch__thumb': {
											w: '16px',
											h: '16px',
											bg: 'white',
											boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
											_checked: {
												transform: 'translateX(14px)',
											},
										}
									}}
								/>
							</HStack>
							{canCreateMission && (
								<Button
									leftIcon={<Img src={plusSquaredIcon} w="16px" h="16px" />}
									bg="#006133"
									color="white"
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
									_hover={{ bg: '#004f2aff' }}
									w={{ base: '100%', md: 'auto' }}
									onClick={handleCreateMissionClick}
								>
									Créer un dossier de régulation
								</Button>
							)}
						</>
					) : isAvisPage ? (
						<>
							<HStack
								spacing="12px"
								bg="gray.50"
								px="12px"
								py="6px"
								borderRadius="12px"
								border="1px solid"
								borderColor="gray.200"
								boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
								transition="all 0.2s"
								_hover={{ borderColor: "gray.300", bg: "gray.100" }}
							>
								<Text fontSize="12px" color="gray.600" fontWeight="600" fontFamily="Poppins, sans-serif" whiteSpace="nowrap">
									Afficher les stats
								</Text>
								<Switch
									isChecked={showStats}
									onChange={(e) => setShowStats(e.target.checked)}
									size="sm"
									sx={{
										'& .chakra-switch__track': {
											w: '34px',
											h: '20px',
											bg: showStats ? '#006133' : '#E2E8F0',
											_focus: { boxShadow: 'none' },
										},
										'& .chakra-switch__thumb': {
											w: '16px',
											h: '16px',
											bg: 'white',
											boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
											_checked: {
												transform: 'translateX(14px)',
											},
										}
									}}
								/>
							</HStack>
							{canCreateMission && (
								<Button
									leftIcon={<Img src={plusSquaredIcon} w="16px" h="16px" />}
									bg="#006133"
									color="white"
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
									_hover={{ bg: '#004f2aff' }}
									w={{ base: '100%', md: 'auto' }}
									onClick={handleCreateMissionClick}
								>
									Créer un dossier de régulation	
								</Button>
							)}
						</>
					) : isReclamationsPage ? (
						<>
							<HStack
								spacing="12px"
								bg="gray.50"
								px="12px"
								py="6px"
								borderRadius="12px"
								border="1px solid"
								borderColor="gray.200"
								boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
								transition="all 0.2s"
								_hover={{ borderColor: "gray.300", bg: "gray.100" }}
							>
								<Text fontSize="12px" color="gray.600" fontWeight="600" fontFamily="Poppins, sans-serif" whiteSpace="nowrap">
									Afficher les stats
								</Text>
								<Switch
									isChecked={showStats}
									onChange={(e) => setShowStats(e.target.checked)}
									size="sm"
									sx={{
										'& .chakra-switch__track': {
											w: '34px',
											h: '20px',
											bg: showStats ? '#006133' : '#E2E8F0',
											_focus: { boxShadow: 'none' },
										},
										'& .chakra-switch__thumb': {
											w: '16px',
											h: '16px',
											bg: 'white',
											boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
											_checked: {
												transform: 'translateX(14px)',
											},
										}
									}}
								/>
							</HStack>
							{onCreateReclamation && hasPermission('reclamations_create') && (
								<Button
									leftIcon={<Img src={plusSquaredIcon} w="16px" h="16px" />}
									bg="#E53E3E"
									color="white"
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
									_hover={{ bg: '#c53030' }}
									w={{ base: '100%', md: 'auto' }}
									onClick={onCreateReclamation}
								>
									Nouvelle réclamation
								</Button>
							)}
						</>
					) : isPointageSuiviPage ? (
						<>
							<HStack
								spacing="12px"
								bg="gray.50"
								px="12px"
								py="6px"
								borderRadius="12px"
								border="1px solid"
								borderColor="gray.200"
								boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
								transition="all 0.2s"
								_hover={{ borderColor: "gray.300", bg: "gray.100" }}
							>
								<Text fontSize="12px" color="gray.600" fontWeight="600" fontFamily="Poppins, sans-serif" whiteSpace="nowrap">
									Afficher les stats
								</Text>
								<Switch
									isChecked={showStats}
									onChange={(e) => setShowStats(e.target.checked)}
									size="sm"
									sx={{
										'& .chakra-switch__track': {
											w: '34px',
											h: '20px',
											bg: showStats ? '#006133' : '#E2E8F0',
											_focus: { boxShadow: 'none' },
										},
										'& .chakra-switch__thumb': {
											w: '16px',
											h: '16px',
											bg: 'white',
											boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
											_checked: {
												transform: 'translateX(14px)',
											},
										}
									}}
								/>
							</HStack>
							{canCreateMission && (
								<Button
									leftIcon={<Img src={plusSquaredIcon} w="16px" h="16px" />}
									bg="#006133"
									color="white"
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
									_hover={{ bg: '#004f2aff' }}
									w={{ base: '100%', md: 'auto' }}
									onClick={handleCreateMissionClick}
								>
									Créer un dossier de régulation
								</Button>
							)}
						</>
					) : isLogsPage ? (
						<></>
					) : isProfilPage ? (
						<HStack spacing="10px">
							<Text fontSize="13px" fontWeight="500" color="#455879" fontFamily="Poppins, sans-serif">
								Paramètres
							</Text>
						</HStack>
					) : (
						<>
							<HStack
								spacing="12px"
								bg="gray.50"
								px="12px"
								py="6px"
								borderRadius="12px"
								border="1px solid"
								borderColor="gray.200"
								boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
								transition="all 0.2s"
								_hover={{ borderColor: "gray.300", bg: "gray.100" }}
							>
								<Text fontSize="12px" color="gray.600" fontWeight="600" fontFamily="Poppins, sans-serif" whiteSpace="nowrap">
									Afficher les stats
								</Text>
								<Switch
									isChecked={showStats}
									onChange={(e) => setShowStats(e.target.checked)}
									size="sm"
									sx={{
										'& .chakra-switch__track': {
											w: '34px',
											h: '20px',
											bg: showStats ? '#006133' : '#E2E8F0',
											_focus: { boxShadow: 'none' },
										},
										'& .chakra-switch__thumb': {
											w: '16px',
											h: '16px',
											bg: 'white',
											boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.15)',
											_checked: {
												transform: 'translateX(14px)',
											},
										}
									}}
								/>
							</HStack>
							{canCreateMission && (
								<Button
									leftIcon={<Img src={plusSquaredIcon} w="16px" h="16px" />}
									bg="#006133"
									color="white"
									h="36px"
									borderRadius="6px"
									px="10px"
									py="9px"
									fontSize="13px"
									fontWeight="500"
									fontFamily="Poppins, sans-serif"
									lineHeight="12px"
									_hover={{ bg: '#004f2aff' }}
									w={{ base: '100%', md: 'auto' }}
									onClick={handleCreateMissionClick}
								>
									Créer un dossier de régulation
								</Button>
							)}
						</>
					)}
				</Flex>
			</Flex>


		</Box>
	);
}

AdminNavbar.propTypes = {
	brandText: PropTypes.string,
	variant: PropTypes.string,
	secondary: PropTypes.bool,
	fixed: PropTypes.bool,
	onOpen: PropTypes.func
};
